import { SampleFile } from "projen";
import { NodeProject } from "projen/lib/javascript";

/**
 * Options for {@link addStorybook}.
 */
export interface AddStorybookOptions {
  /**
   * Install `@storybook/addon-docs` and register it in
   * `.storybook/main.ts`.
   *
   * @default true
   */
  readonly withDocs?: boolean;
}

/**
 * Wire Storybook 9 (Vite framework) into a {@link NodeProject}.
 *
 * Adds the Storybook + `@storybook/react-vite` dev deps, registers
 * `storybook` and `build-storybook` projen tasks (with matching package
 * scripts), and emits the canonical `.storybook/main.ts` /
 * `.storybook/preview.ts` config pair. The preview imports the React
 * site's Tailwind entry (`src/index.css`) so stories pick up the same
 * theme as the running app.
 *
 * Designed to be called against a `ReactViteSiteProject`, but accepts
 * any `NodeProject` — the integration is intentionally framework-light
 * so it can layer onto adjacent React + Vite scaffolds without
 * subclassing.
 *
 * @param project - The project to wire Storybook into.
 * @param options - Optional configuration. Defaults to `withDocs: true`.
 */
export function addStorybook(
  project: NodeProject,
  options: AddStorybookOptions = {},
): void {
  const withDocs = options.withDocs ?? true;

  project.addDevDeps(
    "storybook",
    "@storybook/react@^9",
    "@storybook/react-vite@^9",
    ...(withDocs ? ["@storybook/addon-docs@^9"] : []),
  );

  /**
   * Tasks
   */
  project.tasks.addTask("storybook", {
    description: "Run Storybook (Vite)",
    steps: [{ exec: "storybook dev -p 6006" }],
  });
  project.tasks.addTask("build-storybook", {
    description: "Build static Storybook",
    steps: [{ exec: "storybook build" }],
  });

  /**
   * Package scripts so `pnpm storybook` / `pnpm build-storybook` work
   * inside the app.
   */
  project.setScript("storybook", "npx projen storybook");
  project.setScript("build-storybook", "npx projen build-storybook");

  /**
   * Config files.
   */
  new SampleFile(project, ".storybook/main.ts", {
    contents: `import type { StorybookConfig } from '@storybook/react-vite';

const config: StorybookConfig = {
  stories: ['../src/**/*.mdx', '../src/**/*.stories.@(js|jsx|ts|tsx)'],
  addons: [${withDocs ? `'@storybook/addon-docs'` : ""}],
  framework: { name: '@storybook/react-vite', options: {} },
};
export default config;
`,
  });

  new SampleFile(project, ".storybook/preview.ts", {
    contents: `import '../src/index.css';
const preview = { parameters: { controls: { expanded: true } } };
export default preview;
`,
  });
}
